/** Render-only release history views, separated from query ownership for SSR coverage. */

import type { ReactNode, RefObject } from 'react';
import { Link } from 'react-router';
import { Button, UnderlineNav } from '@primer/react';
import { ArrowLeftIcon, ChevronLeftIcon, ChevronRightIcon, LinkExternalIcon } from '@primer/octicons-react';

import type { DeploymentAttemptView, ReleaseItemView, ReleaseOperationView } from '../../client/types.js';
import {
  externalReleaseLinkProps,
  releaseDetailHref,
  releaseDurationText,
  releaseListHref,
  releaseListPagination,
  releaseStatusPresentation,
  releaseText,
  releaseTimestampPresentation,
  type ReleaseListMode,
  type ReleaseQueryPhase,
} from '../../logic/releases.js';
import { Chip, QueryPhase, Section } from './state.js';

export interface ReleaseHistoryViewProps {
  readonly listState: { readonly mode: ReleaseListMode; readonly offset: number };
  readonly phase: ReleaseQueryPhase;
  readonly error: unknown;
  readonly operations: readonly ReleaseOperationView[];
  readonly pagination: { readonly limit: number; readonly offset: number; readonly returned: number } | null;
  readonly onRetry: () => void;
}

export function ReleaseHistoryView({
  listState,
  phase,
  error,
  operations,
  pagination,
  onRetry,
}: ReleaseHistoryViewProps): ReactNode {
  return (
    <div className="stack releases-screen">
      <UnderlineNav aria-label="Release history views" variant="flush" className="release-tabs">
        <UnderlineNav.Item
          as={Link}
          to={releaseListHref('releases', 0)}
          {...(listState.mode === 'releases' ? { 'aria-current': 'page' as const } : {})}
        >
          Production releases
        </UnderlineNav.Item>
        <UnderlineNav.Item
          as={Link}
          to={releaseListHref('deployments', 0)}
          {...(listState.mode === 'deployments' ? { 'aria-current': 'page' as const } : {})}
        >
          All deployments
        </UnderlineNav.Item>
      </UnderlineNav>

      <Section
        label={listState.mode === 'releases' ? 'Production releases' : 'All deployments'}
        lede={
          listState.mode === 'releases'
            ? 'Production business records and the immutable deployments behind them.'
            : 'Stage and production deployment history, including every recorded attempt.'
        }
      >
        {phase === 'ready' ? (
          <ReleaseOperationList mode={listState.mode} offset={listState.offset} operations={operations} />
        ) : (
          <QueryPhase
            isPending={phase === 'loading'}
            error={phase === 'error' ? error : null}
            isEmpty={phase === 'empty'}
            source={listState.mode === 'releases' ? 'production releases' : 'deployments'}
            emptyTitle={listState.mode === 'releases' ? 'No production releases yet' : 'No deployments yet'}
            emptyDetail={
              listState.offset > 0
                ? 'This page is empty. Go to the previous page to continue browsing history.'
                : listState.mode === 'releases'
                  ? 'Production releases appear here after a release agent records one.'
                  : 'Stage and production deployments appear here after a release agent records one.'
            }
            onRetry={onRetry}
            rows={4}
          />
        )}

        {pagination === null ? null : <ReleasePagination mode={listState.mode} pagination={pagination} />}
      </Section>
    </div>
  );
}

function ReleaseOperationList({
  mode,
  offset,
  operations,
}: {
  readonly mode: ReleaseListMode;
  readonly offset: number;
  readonly operations: readonly ReleaseOperationView[];
}): ReactNode {
  return (
    <ol className="release-list" role="list" aria-label={mode === 'releases' ? 'Production releases' : 'Deployments'}>
      {operations.map((operation, index) => (
        <ReleaseOperationRow
          key={`${releaseText(operation.deployment?.id, 'deployment')}-${String(index)}`}
          mode={mode}
          offset={offset}
          operation={operation}
        />
      ))}
    </ol>
  );
}

function ReleaseOperationRow({
  mode,
  offset,
  operation,
}: {
  readonly mode: ReleaseListMode;
  readonly offset: number;
  readonly operation: ReleaseOperationView;
}): ReactNode {
  const deployment = operation.deployment;
  const release = operation.release;
  const production = mode === 'releases' && release !== null;
  const id = production ? release.id : deployment.id;
  const title = production ? release.title : deployment.title;
  const status = production ? release.status : deployment.status;
  const actor = production ? release.actor?.roleId : deployment.actor?.roleId;
  const createdAt = production ? release.createdAt : deployment.createdAt;
  const durationMs = production ? release.durationMs : deployment.durationMs;
  const detailPath = releaseDetailHref(production ? 'release' : 'deployment', id, { mode, offset });
  const shownStatus = releaseStatusPresentation(status);
  const target = deployment.target as Partial<typeof deployment.target> | null;
  const publicUrl = externalReleaseLinkProps(target?.publicUrl);

  return (
    <li>
      <article className="release-list-row">
        <header className="release-list-heading">
          <div className="release-list-identity">
            <Link className="release-title-link" to={detailPath} aria-label={`Open ${production ? 'release' : 'deployment'} ${releaseText(id)}`}>
              {releaseText(title)}
            </Link>
            <code className="release-id">{releaseText(id)}</code>
          </div>
          <Chip tone={shownStatus.tone} text={shownStatus.text} label={`Status ${shownStatus.text}`} />
        </header>

        <dl className="release-list-facts">
          <Fact label="Target">
            <span className="release-target-name">{releaseText(target?.displayName)}</span>
            <span>{targetType(target?.type)}</span>
            {publicUrl === null ? (
              target?.publicUrl === null ? <span>Public URL not configured</span> : <span>Public URL unavailable</span>
            ) : (
              <a className="release-external release-wrap-anywhere" {...publicUrl}>
                {publicUrl.href}
                <LinkExternalIcon size={16} aria-hidden />
              </a>
            )}
          </Fact>
          <Fact label="Items">{String(Array.isArray(operation.items) ? operation.items.length : 0)}</Fact>
          <Fact label="Actor role">{releaseText(actor)}</Fact>
          <Fact label="Created"><ReleaseTime value={createdAt} /></Fact>
          <Fact label="Duration">{releaseDurationText(durationMs)}</Fact>
        </dl>
      </article>
    </li>
  );
}

function ReleasePagination({
  mode,
  pagination,
}: {
  readonly mode: ReleaseListMode;
  readonly pagination: { readonly limit: number; readonly offset: number; readonly returned: number };
}): ReactNode {
  const page = releaseListPagination(pagination);
  if (page.previousOffset === null && page.nextOffset === null) return null;
  const first = pagination.returned === 0 ? 0 : pagination.offset + 1;
  const last = pagination.offset + pagination.returned;

  return (
    <nav className="release-pagination" aria-label={`${mode === 'releases' ? 'Release' : 'Deployment'} pages`}>
      <span className="release-page-range" aria-live="polite">
        Showing {String(first)}–{String(last)}
      </span>
      <span className="release-page-actions">
        {page.previousOffset === null ? null : (
          <Button as={Link} to={releaseListHref(mode, page.previousOffset)} leadingVisual={ChevronLeftIcon}>
            Previous
          </Button>
        )}
        {page.nextOffset === null ? null : (
          <Button as={Link} to={releaseListHref(mode, page.nextOffset)} trailingVisual={ChevronRightIcon}>
            Next
          </Button>
        )}
      </span>
    </nav>
  );
}

export interface ReleaseOperationDetailViewProps {
  readonly kind: 'deployment' | 'release';
  readonly id: string | null;
  readonly returnState: { readonly mode: ReleaseListMode; readonly offset: number };
  readonly phase: ReleaseQueryPhase;
  readonly error: unknown;
  readonly operation: ReleaseOperationView | null;
  readonly headingRef: RefObject<HTMLHeadingElement | null>;
  readonly onRetry: () => void;
}

export function ReleaseOperationDetailView({
  kind,
  id,
  returnState,
  phase,
  error,
  operation,
  headingRef,
  onRetry,
}: ReleaseOperationDetailViewProps): ReactNode {
  const terminalError = error ?? new Error(`${kind === 'release' ? 'Release' : 'Deployment'} detail is unavailable.`);
  return (
    <div className="stack release-detail-screen">
      <div className="release-detail-back">
        <Button as={Link} to={releaseListHref(returnState.mode, returnState.offset)} leadingVisual={ArrowLeftIcon}>
          Release history
        </Button>
      </div>

      {phase === 'ready' && operation !== null ? (
        <ReleaseOperationDetail kind={kind} operation={operation} returnState={returnState} headingRef={headingRef} />
      ) : phase === 'loading' ? (
        <QueryPhase isPending error={null} source={`${kind} detail`} onRetry={onRetry} rows={5} />
      ) : (
        <section className="release-detail-state" aria-label={`${kind} detail unavailable`}>
          <h2 ref={headingRef} tabIndex={-1}>
            {kind === 'release' ? 'Release' : 'Deployment'} detail unavailable
          </h2>
          <QueryPhase
            isPending={false}
            error={id === null ? new Error(`No ${kind} id was provided.`) : terminalError}
            source={`${kind} detail`}
            onRetry={onRetry}
            rows={5}
          />
        </section>
      )}
    </div>
  );
}

function ReleaseOperationDetail({
  kind,
  operation,
  returnState,
  headingRef,
}: {
  readonly kind: 'deployment' | 'release';
  readonly operation: ReleaseOperationView;
  readonly returnState: { readonly mode: ReleaseListMode; readonly offset: number };
  readonly headingRef: RefObject<HTMLHeadingElement | null>;
}): ReactNode {
  const items = Array.isArray(operation.items) ? operation.items : [];
  const attempts = Array.isArray(operation.attempts) ? operation.attempts : [];

  return (
    <>
      {kind === 'release' ? (
        <ReleaseOverview release={operation.release} deployment={operation.deployment} returnState={returnState} headingRef={headingRef} />
      ) : (
        <DeploymentOverview operation={operation} returnState={returnState} headingRef={headingRef} />
      )}

      <Section
        label={kind === 'release' ? 'Deployment composition' : 'Immutable items'}
        lede="Pinned task, repository, branch, and commit facts are historical snapshots and cannot be edited here."
      >
        {items.length === 0 ? (
          <p className="release-empty">No immutable items were recorded for this deployment.</p>
        ) : (
          <ol className="release-items" role="list">
            {items.map((item, index) => (
              <ReleaseItem key={`${releaseText(item.id, 'item')}-${String(index)}`} item={item} />
            ))}
          </ol>
        )}
      </Section>

      <Section label="Attempts" lede="Every physical try is shown. Provider logs remain with the provider; only bounded platform evidence appears here.">
        {attempts.length === 0 ? (
          <p className="release-empty">No attempts have been started for this deployment.</p>
        ) : (
          <ol className="release-attempts" role="list">
            {attempts.map((attempt, index) => (
              <ReleaseAttempt key={`${releaseText(attempt.id, 'attempt')}-${String(index)}`} attempt={attempt} />
            ))}
          </ol>
        )}
      </Section>
    </>
  );
}

function ReleaseOverview({
  release,
  deployment,
  returnState,
  headingRef,
}: {
  readonly release: ReleaseOperationView['release'];
  readonly deployment: ReleaseOperationView['deployment'];
  readonly returnState: { readonly mode: ReleaseListMode; readonly offset: number };
  readonly headingRef: RefObject<HTMLHeadingElement | null>;
}): ReactNode {
  if (release === null) {
    return (
      <Section label="Release">
        <h3 ref={headingRef} tabIndex={-1} className="release-missing-heading">Release record unavailable</h3>
        <p className="release-empty">The API did not include the production Release record. Deployment facts are still shown below.</p>
        <DeploymentLink deployment={deployment} returnState={returnState} />
      </Section>
    );
  }
  const status = releaseStatusPresentation(release.status);
  const target = deployment.target as Partial<typeof deployment.target> | null;
  return (
    <Section label="Release" lede="The production business record linked to one immutable deployment.">
      <header className="release-detail-heading">
        <div>
          <h3 ref={headingRef} tabIndex={-1}>{releaseText(release.title)}</h3>
          <code className="release-id">{releaseText(release.id)}</code>
        </div>
        <Chip tone={status.tone} text={status.text} label={`Release status ${status.text}`} />
      </header>
      <dl className="release-detail-facts">
        <Fact label="Actor role">{releaseText(release.actor?.roleId)}</Fact>
        <Fact label="Created"><ReleaseTime value={release.createdAt} /></Fact>
        <Fact label="Updated"><ReleaseTime value={release.updatedAt} /></Fact>
        <Fact label="Completed"><ReleaseTime value={release.completedAt} empty="Not completed" /></Fact>
        <Fact label="Duration">{releaseDurationText(release.durationMs)}</Fact>
        <Fact label="Linked deployment"><DeploymentLink deployment={deployment} returnState={returnState} /></Fact>
        <Fact label="Deployment target">{releaseText(target?.displayName)}</Fact>
        <Fact label="Deployment type">{targetType(target?.type)}</Fact>
        <Fact label="Public URL"><ExternalReleaseLink value={target?.publicUrl} empty="Not configured" /></Fact>
      </dl>
    </Section>
  );
}

function DeploymentOverview({
  operation,
  returnState,
  headingRef,
}: {
  readonly operation: ReleaseOperationView;
  readonly returnState: { readonly mode: ReleaseListMode; readonly offset: number };
  readonly headingRef: RefObject<HTMLHeadingElement | null>;
}): ReactNode {
  const deployment = operation.deployment;
  const status = releaseStatusPresentation(deployment.status);
  const target = deployment.target as Partial<typeof deployment.target> | null;
  return (
    <Section label="Deployment" lede="An immutable logical operation. Attempts below record each physical try.">
      <header className="release-detail-heading">
        <div>
          <h3 ref={headingRef} tabIndex={-1}>{releaseText(deployment.title)}</h3>
          <code className="release-id">{releaseText(deployment.id)}</code>
        </div>
        <Chip tone={status.tone} text={status.text} label={`Deployment status ${status.text}`} />
      </header>
      <dl className="release-detail-facts">
        <Fact label="Target">{releaseText(target?.displayName)}</Fact>
        <Fact label="Target slug"><code>{releaseText(target?.slug)}</code></Fact>
        <Fact label="Type">{targetType(target?.type)}</Fact>
        <Fact label="Public URL"><ExternalReleaseLink value={target?.publicUrl} empty="Not configured" /></Fact>
        <Fact label="Actor role">{releaseText(deployment.actor?.roleId)}</Fact>
        <Fact label="Created"><ReleaseTime value={deployment.createdAt} /></Fact>
        <Fact label="Updated"><ReleaseTime value={deployment.updatedAt} /></Fact>
        <Fact label="Completed"><ReleaseTime value={deployment.completedAt} empty="Not completed" /></Fact>
        <Fact label="Duration">{releaseDurationText(deployment.durationMs)}</Fact>
        <Fact label="Instruction reference"><span className="release-wrap-anywhere">{releaseText(deployment.instructionReference)}</span></Fact>
        <Fact label="Client request id"><code>{releaseText(deployment.clientRequestId)}</code></Fact>
        <Fact label="Related Release">
          {operation.release === null ? (
            <span>Not a production release</span>
          ) : (
            <Link className="release-internal-link" to={releaseDetailHref('release', operation.release.id, returnState)}>
              {releaseText(operation.release.title)}
            </Link>
          )}
        </Fact>
      </dl>
    </Section>
  );
}

function DeploymentLink({
  deployment,
  returnState,
}: {
  readonly deployment: ReleaseOperationView['deployment'];
  readonly returnState: { readonly mode: ReleaseListMode; readonly offset: number };
}): ReactNode {
  return (
    <Link className="release-internal-link release-wrap-anywhere" to={releaseDetailHref('deployment', deployment.id, returnState)}>
      {releaseText(deployment.title)} · {releaseText(deployment.id)}
    </Link>
  );
}

function ReleaseItem({ item }: { readonly item: ReleaseItemView }): ReactNode {
  const task = item.task as Partial<ReleaseItemView['task']> | null;
  return (
    <li className="release-item">
      <header className="release-item-heading">
        <h3>{releaseText(task?.label, 'Task unavailable')}</h3>
        <span>Immutable item {String(Number.isFinite(item.order) ? item.order + 1 : '')}</span>
      </header>
      <dl className="release-item-facts">
        <Fact label="Task URL"><ExternalReleaseLink value={task?.url} empty="Task link unavailable" showUrl /></Fact>
        <Fact label="Task binding"><code>{releaseText(task?.bindingId)}</code></Fact>
        <Fact label="Task revision"><code>{releaseText(task?.revisionDigest)}</code></Fact>
        <Fact label="Repository"><code>{releaseText(item.repository)}</code></Fact>
        <Fact label="Source branch"><code>{releaseText(item.sourceBranch)}</code></Fact>
        <Fact label="Target base branch"><code>{releaseText(item.targetBaseBranch)}</code></Fact>
        <Fact label="Pinned SHA"><code>{releaseText(item.pinnedSha)}</code></Fact>
        <Fact label="Change URL"><ExternalReleaseLink value={item.changeUrl} empty="Not provided" showUrl /></Fact>
        <Fact label="Recorded"><ReleaseTime value={item.createdAt} /></Fact>
      </dl>
    </li>
  );
}

function ReleaseAttempt({ attempt }: { readonly attempt: DeploymentAttemptView }): ReactNode {
  const status = releaseStatusPresentation(attempt.status);
  const provider = attempt.provider as Partial<DeploymentAttemptView['provider']> | null;
  const heads = sourceHeadEntries(attempt.sourceHeads);
  const evidence = asRecord(attempt.evidence);
  const error = asRecord(attempt.error);
  const checks = Array.isArray(evidence?.['checks']) ? evidence['checks'] : [];

  return (
    <li className="release-attempt">
      <header className="release-attempt-heading">
        <div>
          <h3>Attempt {typeof attempt.attemptNumber === 'number' ? String(attempt.attemptNumber) : 'unknown'}</h3>
          <code className="release-id">{releaseText(attempt.id)}</code>
        </div>
        <Chip tone={status.tone} text={status.text} label={`Attempt status ${status.text}`} />
      </header>
      <dl className="release-attempt-facts">
        <Fact label="Created"><ReleaseTime value={attempt.createdAt} /></Fact>
        <Fact label="Updated"><ReleaseTime value={attempt.updatedAt} /></Fact>
        <Fact label="Started"><ReleaseTime value={attempt.startedAt} /></Fact>
        <Fact label="Finished"><ReleaseTime value={attempt.finishedAt} empty="Not finished" /></Fact>
        <Fact label="Duration">{releaseDurationText(attempt.durationMs)}</Fact>
        <Fact label="Provider run">
          <ExternalReleaseLink value={provider?.runUrl} empty={releaseText(provider?.runId, 'Not provided')}>
            {releaseText(provider?.runId, 'Open provider run')}
          </ExternalReleaseLink>
        </Fact>
      </dl>

      <div className="release-attempt-detail-grid">
        <div className="release-attempt-detail">
          <h4>Source heads</h4>
          {heads.length === 0 ? (
            <p className="release-empty">No source heads were recorded.</p>
          ) : (
            <dl className="release-source-heads">
              {heads.map(([repository, sha]) => (
                <Fact key={repository} label={repository}><code>{sha}</code></Fact>
              ))}
            </dl>
          )}
        </div>

        <div className="release-attempt-detail">
          <h4>Platform evidence</h4>
          {evidence === null ? (
            <p className="release-empty">No bounded evidence was reported.</p>
          ) : (
            <>
              <p>{releaseText(evidence['summary'])}</p>
              {checks.length === 0 ? null : (
                <ul className="release-checks" role="list">
                  {checks.map((candidate, index) => {
                    const check = asRecord(candidate);
                    return (
                      <li key={`${releaseText(check?.['kind'], 'check')}-${String(index)}`}>
                        <strong>{releaseText(check?.['label'], 'Check')}</strong>
                        <span>{releaseText(check?.['status'], 'UNKNOWN')}</span>
                        <span>{releaseText(check?.['summary'])}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}
        </div>
      </div>

      {error === null ? null : (
        <div className="release-error" role="status">
          <h4>Platform error summary</h4>
          <p><strong>{releaseText(error['category'], 'Unknown error')}</strong> · {releaseText(error['summary'])}</p>
          {asRecord(error['check']) === null ? null : (
            <p>Related check: {releaseText(asRecord(error['check'])?.['label'])}</p>
          )}
        </div>
      )}
    </li>
  );
}

function Fact({ label, children }: { readonly label: string; readonly children: ReactNode }): ReactNode {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function ReleaseTime({ value, empty = 'Unavailable' }: { readonly value: unknown; readonly empty?: string }): ReactNode {
  if (value === null) return <span>{empty}</span>;
  const shown = releaseTimestampPresentation(value);
  return shown.dateTime === null ? <span>{shown.text}</span> : <time dateTime={shown.dateTime}>{shown.text}</time>;
}

function ExternalReleaseLink({
  value,
  empty,
  showUrl = false,
  children,
}: {
  readonly value: unknown;
  readonly empty: string;
  readonly showUrl?: boolean;
  readonly children?: ReactNode;
}): ReactNode {
  const props = externalReleaseLinkProps(value);
  if (props === null) return <span>{empty}</span>;
  return (
    <a className="release-external release-wrap-anywhere" {...props}>
      {showUrl ? props.href : children ?? props.href}
      <LinkExternalIcon size={16} aria-hidden />
    </a>
  );
}

function targetType(value: unknown): string {
  if (value === 'production') return 'Production';
  if (value === 'stage') return 'Stage';
  return 'Unknown type';
}

function asRecord(value: unknown): Readonly<Record<string, unknown>> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Readonly<Record<string, unknown>>)
    : null;
}

function sourceHeadEntries(value: unknown): readonly (readonly [string, string])[] {
  const record = asRecord(value);
  if (record === null) return [];
  return Object.entries(record)
    .filter((entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1].trim().length > 0)
    .sort(([left], [right]) => left.localeCompare(right));
}
